import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, ExternalLink, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Caption, Card, CompanyLogo, Empty, NativeSelect, PageHeader, ScoreRing, Spinner } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { AutoSchedule } from "@/components/AutoSchedule";
import { AutoBoost } from "@/components/AutoBoost";
import { usePickBeforeApply } from "@/components/AutoPickPrompt";
import { noteApply } from "@/lib/streak";
import { LiveProgress } from "@/components/AutoProgress";
import { PreApplyCheck, needsPreCheck } from "@/components/PreApplyCheck";
import { useAuth } from "@/context/AuthContext";
import { api, enc, errMsg } from "@/lib/api";

const COUNTS = [3, 5, 10].map((n) => ({ value: String(n), label: `${n} jobs` }));

const Item = ({ it, onAction }) => {
  const job = it.job || {};
  const done = ["applied", "skipped"].includes(it.status);
  const [preCheck, setPreCheck] = useState(false);
  const doApply = async () => {
    noteApply();
    if (job.apply_link) window.open(job.apply_link, "_blank", "noopener");
    if (it.cover_letter) { try { await navigator.clipboard.writeText(it.cover_letter); toast.success("Cover letter copied"); } catch {} }
    onAction(it.job_id, "applied");
  };
  const { start: pickApply, dialog: pickDialog } = usePickBeforeApply(doApply);
  const applyNow = () => (needsPreCheck(it.job_id) ? setPreCheck(true) : pickApply(it.job_id));
  return (
    <Card className="fade-up" data-testid={`auto-item-${it.job_id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <CompanyLogo src={job.logo} name={job.company} />
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg text-zinc-100">{job.title}</h3>
          <p className="text-sm text-zinc-400">{job.company} {job.location && `· ${job.location}`}</p>
          {it.reason && <p className="mt-2 text-sm text-zinc-500">{it.reason}</p>}
        </div>
        {it.score != null && <ScoreRing score={it.score} />}
      </div>
      {it.highlights?.length > 0 && <div className="mt-4"><Rich value={it.highlights} /></div>}
      <details className="mt-4 rounded-lg border border-[#27272A] bg-[#09090B] p-3">
        <summary className="cursor-pointer text-sm text-[#F3C649]">Cover letter & screening answers</summary>
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{it.cover_letter}</p>
        {(it.answers || []).map((a, i) => <div key={i} className="mt-3"><Caption>{a.q}</Caption><p className="mt-1 text-sm text-zinc-300">{a.a}</p></div>)}
      </details>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {done ? <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500" data-testid="auto-item-status">{it.status}</span> : (
          <>
            <Button onClick={applyNow} data-testid={`auto-apply-now-${it.job_id}`}><ExternalLink className="h-4 w-4" />Apply now</Button>
            <AutoBoost job={{ ...job, id: it.job_id }} />
            <Button variant="ghost" onClick={() => onAction(it.job_id, "skip")} data-testid={`auto-skip-${it.job_id}`}><SkipForward className="h-4 w-4" />Skip</Button>
            {preCheck && <PreApplyCheck job={{ ...job, id: it.job_id }} open={preCheck} onClose={() => setPreCheck(false)} onApply={() => pickApply(it.job_id)} />}
            {pickDialog}
          </>
        )}
      </div>
    </Card>
  );
};

export default function AutoApply() {
  const { profile } = useAuth();
  const [form, setForm] = useState({ query: profile?.headline || "", remote: false, count: "5", min_score: 60 });
  const [state, setState] = useState(null);
  const timer = useRef(null);
  const [lastAt, setLastAt] = useState(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/auto-apply");
      setState(data);
      setLastAt(Date.now());
      clearTimeout(timer.current);
      if (data?.batch?.status === "running") timer.current = setTimeout(poll, 3000);
    } catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e)); setState({}); }
  }, []);
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, [poll]);

  const run = async () => {
    try { await api.post("/auto-apply/run", { ...form, count: Number(form.count) }); toast.success("Agent started"); poll(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (jobId, kind) => {
    try { await api.post(`/auto-apply/${enc(jobId)}/${kind}`); setState((s) => ({ ...s, items: s.items.map((i) => (i.job_id === jobId ? { ...i, status: kind === "skip" ? "skipped" : "applied" } : i)) })); } catch (e) { toast.error(errMsg(e)); }
  };

  const running = state?.batch?.status === "running";
  return (
    <div>
      <PageHeader title="Auto-Apply Agent" subtitle="The agent finds matching roles, writes the cover letter and drafts screening answers. You review and send." />
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card>
            <div className="grid gap-4 md:grid-cols-[1fr_auto_auto] md:items-end">
              <div className="space-y-1.5"><Label className="text-xs text-zinc-400">Role</Label><Input value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} className="bg-[#09090B]" data-testid="auto-query-input" /></div>
              <NativeSelect value={form.count} onChange={(v) => setForm({ ...form, count: v })} options={COUNTS} data-testid="auto-count-select" />
              <label className="flex h-10 items-center gap-2 text-sm text-zinc-300"><Switch checked={form.remote} onCheckedChange={(v) => setForm({ ...form, remote: v })} data-testid="auto-remote-switch" />Remote</label>
            </div>
            <div className="mt-5 space-y-2">
              <div className="flex justify-between text-xs text-zinc-400"><span>Minimum match score</span><span className="font-mono text-[#F3C649]" data-testid="auto-min-score">{form.min_score}</span></div>
              <Slider value={[form.min_score]} min={0} max={100} step={5} onValueChange={([v]) => setForm({ ...form, min_score: v })} data-testid="auto-min-score-slider" />
            </div>
            <Button onClick={run} disabled={running || !form.query} className="mt-6 w-full" data-testid="auto-run-button">
              {running ? <><Spinner />Agent is working...</> : <><Bot className="h-4 w-4" />Run agent</>}
            </Button>
          </Card>
          {running && <LiveProgress batch={state.batch} found={state.items?.length || 0} target={Number(form.count)} at={lastAt} />}
          {!state ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : state.items?.length ? (
            <div className="space-y-4" data-testid="auto-items">{state.items.map((it) => <Item key={it.job_id} it={it} onAction={act} />)}</div>
          ) : !running && <Empty icon={Bot} title="No batch yet" text="Run the agent to get a reviewed batch of applications." />}
        </div>
        <AutoSchedule />
      </div>
    </div>
  );
}
